import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Link } from 'react-router-dom';
import { getBuilderTimelapses, getCapById, getUserById, getMonitors } from '../utils/bedrockAPI';
import { Player, BigPlayButton, ControlBar, VolumeMenuButton } from 'video-react';
import './TimelapseList.css';

export default function TimelapseList(props) {
  const builderId = props.match.params.builderid;
  const [timelapses, setTimelapses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [caps, setCaps] = useState({});
  const [users, setUsers] = useState({});
  const [isMounted, setIsMounted] = useState(true);
  const [expandedTimelapseId, setExpandedTimelapseId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [monitorLookups, setMonitorLookups] = useState({});

  useEffect(() => {
    if (isMounted) {
      loadTimelapses();
    }
    return () => {
      setIsMounted(false);
    };
  }, [builderId, isMounted]);

  const loadTimelapses = async () => {
    try {
      const timelapseList = await getBuilderTimelapses(builderId);
      timelapseList.sort((a, b) => new Date(b.addDate) - new Date(a.addDate));
      setTimelapses(timelapseList);

      const capPromises = timelapseList.map(timelapse => getCapById(timelapse.capId));
      const capResults = await Promise.all(capPromises);

      const validCaps = capResults.filter(cap => cap);
      const monitorPromises = validCaps.map(cap => getMonitors(cap.capid));
      const monitorResults = await Promise.all(monitorPromises);

      const capsMap = {};
      const lookupsMap = {};
      validCaps.forEach((cap, index) => {
        capsMap[cap.capid] = {
          ...cap,
          monitors: monitorResults[index],
        };

        const monitorLookup = {};
        Object.entries(monitorResults[index] || {}).forEach(([direction, monitors]) => {
          Object.values(monitors).forEach(monitor => {
            monitorLookup[monitor.monitorId] = direction;
          });
        });
        lookupsMap[cap.capid] = monitorLookup;
      });
      setCaps(capsMap);
      setMonitorLookups(lookupsMap);

      const userPromises = timelapseList.map(timelapse => getUserById(timelapse.requestedBy));
      const userResults = await Promise.all(userPromises);
      const usersMap = {};
      userResults.forEach(user => {
        if (user) {
          usersMap[user.userId] = user;
        }
      });
      setUsers(usersMap);

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading timelapses:', error);
      setIsLoading(false);
    }
  };

  const getMonitorName = (cap, monitorId) => {
    if (!cap) return 'Unknown';

    const monitorTypes = {
      house: 1,
      neighbor: 2,
      street: 3,
      driveway: 4,
    };

    const lookup = monitorLookups[cap.capid];
    if (!lookup) return 'Unknown';

    const direction = lookup[monitorId];
    return direction ? monitorTypes[direction] : 'Unknown';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleRowClick = (timelapse) => {
    if (timelapse.status === 'COMPLETED' && timelapse.url) {
      setExpandedTimelapseId(
        expandedTimelapseId === timelapse.timelapseId ? null : timelapse.timelapseId
      );
    }
  };

  const translateS3Url = (url) => {
    const key = url.split('bedrockwireless/')[1];
    return `https://bedrockwireless.s3.us-east-1.amazonaws.com/${key}`;
  };

  const handleCopySuccess = () => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const translateTimelapseStatus = (status) => {
    switch (status) {
      case 'INITIATED':
      case 'STARTED':
        return 'initiated';
      case 'COMPLETED':
        return 'completed';
      case 'ERROR':
        return 'failed';
      case 'DOWNLOADING':
        return 'downloading';
      case 'PROCESSING':
      case 'WATERMARKING':
        return 'processing';
      case 'UPLOADING':
        return 'uploading';
      default:
        return 'unknown';
    }
  };

  const renderSkeletonRows = () => (
    [...Array(5)].map((_, index) => (
      <tr key={index}>
        {[...Array(6)].map((__, cellIndex) => (
          <td key={cellIndex}>
            <div className="timelapse-skeleton" />
          </td>
        ))}
      </tr>
    ))
  );

  return (
    <div className="timelapse-list-page">
      <header className="timelapse-list-header">
        <nav className="brand-breadcrumb">
          <Link to="/dashboard">Dashboard</Link>
          <span className="brand-breadcrumb-sep">/</span>
          <Link to={`/cameramanagement/${builderId}`}>Camera Management</Link>
          <span className="brand-breadcrumb-sep">/</span>
          <span className="brand-breadcrumb-current">Timelapses</span>
        </nav>
        <div className="timelapse-list-title-row">
          <h1 className="timelapse-list-title">Timelapses</h1>
          {/* <Link to={`/cameramanagement/${builderId}`} className="btn btn-brand-outline btn-compact">
            Back to Camera Management
          </Link> */}
        </div>
      </header>

      <section className="timelapse-list-panel">
        <div className="timelapse-table-wrap">
          <table className="timelapse-table">
            <thead>
              <tr>
                <th>Timelapse Name</th>
                <th>Camera</th>
                <th>View</th>
                <th>Date Range</th>
                <th>Status</th>
                <th>Requester</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? renderSkeletonRows() : timelapses.map(timelapse => (
                <React.Fragment key={timelapse.timelapseId}>
                  <tr
                    className={
                      timelapse.status === 'COMPLETED' && timelapse.url
                        ? 'timelapse-row--expandable'
                        : ''
                    }
                    onClick={() => handleRowClick(timelapse)}
                  >
                    <td>{timelapse.name}</td>
                    <td>
                      <Link
                        to={`/caps/${timelapse.capId}`}
                        className="timelapse-cap-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {caps[timelapse.capId]?.name || 'Unknown'}
                      </Link>
                    </td>
                    <td>{getMonitorName(caps[timelapse.capId], timelapse.monitorId)}</td>
                    <td>
                      {formatDate(timelapse.startDate)} – {formatDate(timelapse.endDate)}
                    </td>
                    <td>
                      <span className={`timelapse-status-badge timelapse-status-badge--${translateTimelapseStatus(timelapse.status)}`}>
                        {translateTimelapseStatus(timelapse.status)}
                      </span>
                    </td>
                    <td>
                      {users[timelapse.requestedBy]?.firstName || 'Unknown'}
                      {users[timelapse.requestedBy]?.lastName
                        ? ` ${users[timelapse.requestedBy].lastName[0]}.`
                        : ''}
                    </td>
                  </tr>
                  {expandedTimelapseId === timelapse.timelapseId
                    && timelapse.status === 'COMPLETED'
                    && timelapse.url && (
                    <tr className="timelapse-video-row">
                      <td colSpan="6">
                        <div className="timelapse-video-panel">
                          <div className="timelapse-video-actions">
                            <CopyToClipboard
                              text={translateS3Url(timelapse.url)}
                              onCopy={handleCopySuccess}
                            >
                              <Button className="btn-brand-outline btn-compact">
                                <i className={`fas ${copySuccess ? 'fa-check' : 'fa-copy'}`} />
                                {copySuccess ? 'Copied!' : 'Copy Timelapse Link'}
                              </Button>
                            </CopyToClipboard>
                          </div>
                          <div className="timelapse-video-container">
                            <Player>
                              <source src={translateS3Url(timelapse.url)} />
                              <BigPlayButton position="center" />
                              <ControlBar>
                                <VolumeMenuButton disabled />
                              </ControlBar>
                            </Player>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!isLoading && timelapses.length === 0 && (
                <tr>
                  <td colSpan="6" className="timelapse-empty">
                    No timelapses yet. Create one from Camera Management.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
